import { useRef, useState, type FormEvent } from "react";
import { DataTable, type DataTableColumn } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { exportInventoryCsv, importInventoryCsv } from "../../api/inventoryApi";
import type { InventoryImportRowError } from "../../api/types";
import { BranchSelect } from "../../components/BranchSelect";
import { StatusBanner } from "../../components/StatusBanner";
import { ApiError } from "../../lib/apiClient";
import { canWriteInventory } from "../../roles/permissions";

const ROW_ERROR_COLUMNS: DataTableColumn<InventoryImportRowError, keyof InventoryImportRowError>[] = [
  { key: "row", header: "Row", render: (r) => r.row },
  { key: "message", header: "Problem", render: (r) => r.message },
];

/**
 * `POST /inventory/import`, `GET /inventory/export` (§14.2) — CSV round-trip for the
 * inventory catalog. Import matches on `sku` within tenant+branch (update vs. create) and
 * reports success/failure per row rather than all-or-nothing; export downloads the exact
 * same column set as a starting template.
 */
export function BulkInventoryPage() {
  const { user, ensureFreshToken } = useAuth();
  const canWrite = user ? canWriteInventory(user.role) : false;

  const [importBranchId, setImportBranchId] = useState(user?.assigned_branch_id ?? "");
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [result, setResult] = useState<{ created: number; updated: number; errors: InventoryImportRowError[] } | null>(null);

  const [exportBranchId, setExportBranchId] = useState("");
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  async function handleImport(event: FormEvent) {
    event.preventDefault();
    setImportError(null);
    setResult(null);
    if (!importBranchId) {
      setImportError("Select a branch.");
      return;
    }
    if (!file) {
      setImportError("Choose a CSV file.");
      return;
    }
    setImporting(true);
    try {
      const token = await ensureFreshToken();
      const outcome = await importInventoryCsv(token, importBranchId, file);
      setResult(outcome);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error) {
      setImportError(error instanceof ApiError ? error.message : "Import failed.");
    } finally {
      setImporting(false);
    }
  }

  async function handleExport() {
    setExportError(null);
    setExporting(true);
    try {
      const token = await ensureFreshToken();
      const blob = await exportInventoryCsv(token, exportBranchId || undefined);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "inventory_export.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setExportError(error instanceof Error ? error.message : "Export failed.");
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Bulk Import / Export</h1>
          <p className="page__subtitle">
            CSV round-trip for the inventory catalog — matches existing items by SKU within the chosen
            branch (update), otherwise creates a new item. One bad row is reported and skipped, never a whole-batch
            failure.
          </p>
        </div>
      </div>

      <div className="panel">
        <h2 className="panel__title">Export</h2>
        <p className="muted">
          Columns: sku, name, description, retail_price, wholesale_price, current_stock, low_stock_threshold,
          batch_number, expiry_date, allow_negative_stock, category, unit_of_measure, supplier_id, is_serialized.
        </p>
        {exportError ? <StatusBanner kind="error">{exportError}</StatusBanner> : null}
        <div className="form-grid">
          <BranchSelect id="export-branch" label="Branch" value={exportBranchId} onChange={setExportBranchId} allOptionLabel="All branches" />
        </div>
        <div className="form-actions">
          <button type="button" className="sl-button sl-button--secondary sl-button--md" onClick={() => void handleExport()} disabled={exporting}>
            {exporting ? "Exporting…" : "Download CSV"}
          </button>
        </div>
      </div>

      {canWrite ? (
        <div className="panel">
          <h2 className="panel__title">Import</h2>
          <form onSubmit={handleImport}>
            <div className="form-grid">
              <BranchSelect id="import-branch" label="Branch" value={importBranchId} onChange={setImportBranchId} required />
              <div className="sl-field">
                <label className="sl-field__label" htmlFor="import-file">
                  CSV file
                </label>
                <input
                  ref={fileInputRef}
                  id="import-file"
                  className="sl-input"
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
              </div>
            </div>
            {importError ? <StatusBanner kind="error">{importError}</StatusBanner> : null}
            <div className="form-actions">
              <button type="submit" className="sl-button sl-button--primary sl-button--md" disabled={importing}>
                {importing ? "Importing…" : "Import CSV"}
              </button>
            </div>
          </form>

          {result ? (
            <div style={{ marginTop: 16 }}>
              <StatusBanner kind={result.errors.length > 0 ? "warning" : "info"}>
                Created {result.created}, updated {result.updated}
                {result.errors.length > 0 ? `, ${result.errors.length} row(s) had errors — see below.` : "."}
              </StatusBanner>
              {result.errors.length > 0 ? (
                <DataTable columns={ROW_ERROR_COLUMNS} rows={result.errors} getRowId={(row) => String(row.row)} caption="Import row errors" />
              ) : null}
            </div>
          ) : null}
        </div>
      ) : (
        <StatusBanner kind="info">
          Your role ({user?.role}) has read/export access only. Importing is restricted to OWNER/BRANCH_MANAGER/ECOM_MGR.
        </StatusBanner>
      )}
    </div>
  );
}
